<template>
  <div class="wrapper">
    <form @submit.prevent="" :class="[errorClass, {hide: showSpinner}]">
      <button type="button" class="exit-button" @click="close(false)"><rp-sprite class="exit-icon" name="close"/></button>
      <div class="title">
        <template>
          Manage Notifications
        </template>
      </div>
      <hr class="title-divider">
      <div 
        :class="['editor', {'focused': isEditorFocused}]"
        tabindex="1" 
        ref="editor">
        <editor-content class="editor__content" :editor="editor" />
        <editor-menu-bar :editor="editor">
          <div
            class="menubar">
            <div class="menubar-inner">
              <rp-sprite 
                v-for="(item, index) in editorExtensions"
                :key="index"
                :name=item.icon
                class="menubar__button"
                @click.native=item.function />
            </div>
          </div>
        </editor-menu-bar>
      </div>
      <div class="button-container">
        <rp-button class="submit-button" type="primary" @click="createOrUpdateNotification" />
        <rp-button class="preview-button" @click="previewChanges" type="normal">
          Preview
        </rp-button>
      </div>
    </form>
    <rp-overlay :active="showSpinner">
      <rp-spinner />
    </rp-overlay>
    <rp-overlay :active="notificationActive">
      <div>
        <rp-notifications preview v-model="previewNotification" @exit="closeNotification" />
      </div>
    </rp-overlay>
  </div>
</template>

<script>
  import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
  import {
    Heading,
    Bold,
    Italic,
    HorizontalRule,
    Focus,
    BulletList,
    OrderedList,
    ListItem
  } from 'tiptap-extensions'
  import { validationMixin } from 'vuelidate'
  import { required, requiredIf } from 'vuelidate/lib/validators'
  import { createSystemNotificaton, updateSystemNotification } from '../api/system_notifications'

  import { errorMessages } from '../api/error_messages'
  import form from '../mixins/form'

  import Button from '../components/ui/inputs/Button'
  import Sprite from '../components/ui/Sprite'
  import Overlay from '../components/ui/Overlay'
  import Spinner from '../components/ui/Spinner'
  import Dropdown from './ui/inputs/dropdown/Dropdown'
  import Notifications from './ui/Notification.vue'


  export default {
    components: {
      'rp-button': Button,
      'rp-sprite': Sprite,
      'rp-overlay': Overlay,
      'rp-spinner': Spinner,
      'rp-dropdown': Dropdown,
      'rp-notifications': Notifications,
      EditorContent,
      EditorMenuBar,
    },

    computed: {
      ...errorMessages([
        'name',
        'code'
      ])
    },

    name: 'LanguageForm',

    mixins: [validationMixin, form],

    props: {
      value: Object,
      isCreateForm: Boolean
    },

    data() {
      return {
        showSpinner: false,
        editor: new Editor({
          extensions: [
            new Heading({ levels: [1, 2, 3] }),
            new Bold(),
            new Italic(),
            new HorizontalRule(),
            new BulletList(),
            new ListItem(),
            new OrderedList()
          ],
          content: ``,
          onFocus: () => {
            this.focusEditor()
          },
          onBlur: () => {
            this.blurEditor()
          }
        }),
        isEditorFocused: false,
        editorExtensions: [
          {
            name: "Bold",
            icon: "format_bold",
            function: () => this.editor.commands.bold()
          },
          {
            name: "Italic",
            icon: "format_italic",
            function: () => this.editor.commands.italic()
          },
          {
            name: "Heading 1",
            icon: "format_h1",
            function: () => this.editor.commands.heading({
                level:1
              })
          },
          {
            name: "Heading 2",
            icon: "format_h2",
            function: () => this.editor.commands.heading({
                level:2
              })
          },
          {
            name: "Heading 3",
            icon: "format_h3",
            function: () => this.editor.commands.heading({
                level:3
              })
          },
          {
            name: "Paragraph",
            icon: "format_paragraph",
            function: () => this.editor.commands.paragraph()
          },
          {
            name: "Horisontal rule",
            icon: "format_horisontal",
            function: () => this.editor.commands.horizontal_rule()
          },
          {
            name: "Bullet list",
            icon: "format_bullet",
            function: () => this.editor.commands.bullet_list()
          },
          {
            name: "Ordered list",
            icon: "format_number",
            function: () => this.editor.commands.ordered_list()
          }
        ],
        notificationActive: false,
        previewNotification: []
      }
    },

    mounted () {
      if(this.value) {
        this.editor.setContent(this.value.content)
      }
    },

    beforeDestroy() {
      this.editor.destroy()
    },

    methods: {
      close(shouldUpdateTable) {
        this.$emit('close', shouldUpdateTable)
      },

      focusEditor() {
        this.isEditorFocused = true
      },

      blurEditor() {
        this.isEditorFocused = false
      },

      toggleFormatSize(event) {
        const formatSize = event.value

        if(formatSize == 0) {
          this.editor.commands.paragraph()
        } else {
          this.editor.commands.heading({
            level: formatSize
          })
        }
      },

      createOrUpdateNotification() {
        this.showSpinner = true
        const html = this.editor.getHTML()
        const data = {
          system_notification: {
            content: html
          }
        }
        

        if(this.isCreateForm) {
          createSystemNotificaton(data).then((data) => {
            // FIXME: There should be confirmation window
            this.showSpinner = false
            this.close(true)
          })
        } else {
          data.id = this.value.id
          updateSystemNotification(data).then((data) => {
            this.showSpinner = false
            this.close(true)
          })
        }
      },

      previewChanges() {
        this.previewNotification.push({
          content: this.editor.getHTML()
        })
        this.notificationActive = true
      },

      closeNotification() {
        this.previewNotification = []
        this.notificationActive = false
      }
    },

    validations: {
      resource: {
        name: {required},
        code: {
          required,
          codeFormat(code) {
            return /^[a-z]{2}-[A-Z]{2}$/.test(code)
          }
        },
        file: {
          required: requiredIf(function() {
            return this.isCreateForm
          }) 
        }
      }
    },
  }
</script>

<style lang="sass" scoped>
@import '../styles/main'

.wrapper
  display: flex
  justify-content: center
  width: 100vw

  form
    display: flex
    flex-direction: column
    margin: 0 20px
    position: relative
    width: 90vw
    max-width: 1000px
    transition: opacity 0.2s

    @media screen and (max-width: 640px)
      margin-top: 20%
    
    .language-info
      display: flex
      justify-content: space-between
      margin: 20px 0
      color: $color-text

      .language-info-name
        display: flex
        align-items: center
        margin: 10px 0
        flex: 1

        .info-content
          padding: 5px 0
          margin: 0 70px 0 20px
          color: $color-grey
          flex: 1
          border-bottom: 1px solid $color-grey


    .editor
      border: 1px solid #F2F4F5
      border-radius: 5px
      margin-top: 20px

      &:focus
        outline: none 

      .editor__content
        display: flex
        width: 100%
        height: 300px
        overflow: hidden
        margin-top: 10px

    &.hide
      opacity: 0

    .exit-button
      position: absolute
      right: 5px
      top: 5px
      padding: 10px
      border: 0
      background-color: white

      .exit-icon
        width: 24px
        height: 24px
        fill: grey

    .title
      color: #0C3717
      font-size: 1.8rem
      font-weight: 400
      margin-bottom: 20px

    .title-divider
      margin: 0 -30px
      background-color: #d4d4d4
      height: 1px;
      border: 0

    .button-container
      display: flex
      flex-direction: row-reverse
      margin-top: 30px

      .submit-button
        color: white

      button
        padding: 12px 50px
        height: auto

        @media screen and (max-width: $screen-small)
          padding: 12px 20px

        &.preview-button
          margin-right: 30px
          color: #35373E
          border-color: #35373E
          border-width: 1px
</style>

<style lang="sass">
@import '../styles/main.sass'

.editor
  .ProseMirror
    width: 100%
    height: 100%
    padding: 20px 30px
    overflow-x: hidden

    scrollbar-width: none
    overflow-style: none

    color: $color-text

    ol
      font-size: 14px
      line-height: 28px

    hr
      display: block
      height: 1px
      border: 0
      border-top: 2px solid $color-border-lighter
      margin: 0
      padding: 0

    h1
      font-weight: $font-regular
      margin: 10px 0
    
    h2
      font-size: 14px
      margin: 7px 0

    p
      font-size: 14px
      margin: 0
      line-height: 28px

      &:empty
        height: $pad

      @media screen and (max-width: $screen-small)
        width: 100%
    &:focus
      outline: none

    &::-webkit-scrollbar
      display: none;

      hr
      max-width: 90%


.menubar
  display: flex
  flex-direction: column
  align-items: left
  justify-content: center
  background-color:  #F2F4F5
  height: 60px
  
  .menubar-inner
    display: flex
    flex-direction: row
    align-items: center


    .menubar__button
      height: 26px
      width: 26px
      margin-left: 30px

    &:hover
      cursor: pointer 
</style>


// WEBPACK FOOTER //
// ../raypro_ui/src/components/SystemNotificationEditor.vue